import React, { useState } from 'react';
import { X, Target, TrendingUp, Home, DollarSign, HelpCircle, Info, ChevronRight, Loader2 } from 'lucide-react';
import { investmentCriteriaAPI } from '../api';
import { toast } from 'sonner';

// Tooltip content for each field
const TOOLTIPS = {
  en: {
    // Fix & Flip
    min_net_profit: {
      title: 'Minimum Net Profit',
      description: 'The minimum dollar profit you want to make after all expenses. Most investors target $25,000-$50,000 per deal.',
      typical: '$25,000 - $50,000'
    },
    min_profit_margin: {
      title: 'Minimum Profit Margin',
      description: 'Net profit as a percentage of ARV. This accounts for risk - higher margins provide more cushion for unexpected costs.',
      typical: '12% - 20%'
    },
    min_roi: {
      title: 'Minimum ROI',
      description: 'Return on Investment - profit divided by total cash invested. Higher ROI means better use of your capital.',
      typical: '15% - 30%'
    },
    // Rental
    min_monthly_cash_flow: {
      title: 'Minimum Monthly Cash Flow',
      description: 'Monthly income remaining after all expenses and mortgage payments. This is your passive income per property.',
      typical: '$200 - $500'
    },
    min_cap_rate: {
      title: 'Minimum Cap Rate',
      description: 'Cap Rate measures return before financing. Higher cap rates indicate better yields but often come with higher risk areas.',
      typical: '6% - 10%'
    },
    min_cash_on_cash: {
      title: 'Minimum Cash-on-Cash Return',
      description: 'Annual cash flow divided by total cash invested. Measures how hard your down payment is working for you.',
      typical: '8% - 15%'
    },
    min_dscr: {
      title: 'Minimum DSCR',
      description: 'Debt Service Coverage Ratio - NOI divided by debt payments. 1.25 means income exceeds payments by 25%. Lenders often require 1.2+.',
      typical: '1.2 - 1.5'
    },
    // BRRRR
    min_cash_flow_after_refi: {
      title: 'Min Cash Flow After Refinance',
      description: 'Monthly cash flow after refinancing into a long-term loan. Lower than initial because of higher loan balance.',
      typical: '$150 - $300'
    },
    max_cash_left_in_deal: {
      title: 'Maximum Cash Left in Deal',
      description: 'How much of your initial investment remains after refinancing. Goal is to pull out most/all capital.',
      typical: '$0 - $30,000'
    },
    target_refi_ltv: {
      title: 'Target Refinance LTV',
      description: 'Loan-to-Value at refinance. Higher LTV means more cash out but higher payments. Most lenders max at 75-80%.',
      typical: '70% - 80%'
    }
  },
  es: {
    // Fix & Flip
    min_net_profit: {
      title: 'Ganancia Neta Mínima',
      description: 'La ganancia mínima en dólares que deseas obtener después de todos los gastos. La mayoría de inversionistas buscan $25,000-$50,000 por proyecto.',
      typical: '$25,000 - $50,000'
    },
    min_profit_margin: {
      title: 'Margen de Ganancia Mínimo',
      description: 'Ganancia neta como porcentaje del ARV. Esto considera el riesgo - márgenes más altos proveen más protección para costos inesperados.',
      typical: '12% - 20%'
    },
    min_roi: {
      title: 'ROI Mínimo',
      description: 'Retorno sobre la Inversión - ganancia dividida por el total de efectivo invertido. Mayor ROI significa mejor uso de tu capital.',
      typical: '15% - 30%'
    },
    // Rental
    min_monthly_cash_flow: {
      title: 'Flujo de Caja Mensual Mínimo',
      description: 'Ingreso mensual restante después de todos los gastos y pagos de hipoteca. Este es tu ingreso pasivo por propiedad.',
      typical: '$200 - $500'
    },
    min_cap_rate: {
      title: 'Cap Rate Mínimo',
      description: 'El Cap Rate mide el retorno antes del financiamiento. Cap rates más altos indican mejores rendimientos pero frecuentemente vienen con áreas de mayor riesgo.',
      typical: '6% - 10%'
    },
    min_cash_on_cash: {
      title: 'Retorno Cash-on-Cash Mínimo',
      description: 'Flujo de caja anual dividido por el total de efectivo invertido. Mide qué tan duro está trabajando tu enganche.',
      typical: '8% - 15%'
    },
    min_dscr: {
      title: 'DSCR Mínimo',
      description: 'Ratio de Cobertura de Servicio de Deuda - NOI dividido por pagos de deuda. 1.25 significa que el ingreso excede los pagos por 25%. Prestamistas frecuentemente requieren 1.2+.',
      typical: '1.2 - 1.5'
    },
    // BRRRR
    min_cash_flow_after_refi: {
      title: 'Flujo Mínimo Después de Refinanciar',
      description: 'Flujo de caja mensual después de refinanciar a un préstamo a largo plazo. Menor que el inicial debido al mayor balance del préstamo.',
      typical: '$150 - $300'
    },
    max_cash_left_in_deal: {
      title: 'Máximo Efectivo en el Trato',
      description: 'Cuánto de tu inversión inicial permanece después de refinanciar. El objetivo es recuperar la mayoría/todo el capital.',
      typical: '$0 - $30,000'
    },
    target_refi_ltv: {
      title: 'LTV Objetivo de Refinanciamiento',
      description: 'Loan-to-Value al refinanciar. Mayor LTV significa más efectivo pero pagos más altos. La mayoría de prestamistas máximo 75-80%.',
      typical: '70% - 80%'
    }
  }
};

// Default values
const DEFAULTS = {
  flip: {
    min_net_profit: 30000,
    min_profit_margin: 15,
    min_roi: 20
  },
  rental: {
    min_monthly_cash_flow: 250,
    min_cap_rate: 7,
    min_cash_on_cash: 10,
    min_dscr: 1.25
  },
  brrrr: {
    min_cash_flow_after_refi: 200,
    max_cash_left_in_deal: 25000,
    target_refi_ltv: 75
  }
};

const TooltipButton = ({ field, lang }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltips = TOOLTIPS[lang] || TOOLTIPS.en;
  const tooltip = tooltips[field];
  
  if (!tooltip) return null;
  
  return (
    <div className="relative inline-block">
      <button
        type="button"
        className="text-slate-400 hover:text-blue-500 transition-colors ml-1"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onClick={() => setShowTooltip(!showTooltip)}
      >
        <HelpCircle className="w-4 h-4" />
      </button>
      {showTooltip && (
        <div className="absolute z-50 left-6 top-0 w-64 p-3 bg-slate-800 text-white text-xs rounded-lg shadow-xl">
          <div className="font-semibold mb-1">{tooltip.title}</div>
          <div className="text-slate-300 mb-2">{tooltip.description}</div>
          <div className="text-blue-300">
            {lang === 'es' ? 'Rango típico:' : 'Typical range:'} {tooltip.typical}
          </div>
          <div className="absolute left-[-6px] top-2 w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-r-[6px] border-r-slate-800"></div>
        </div>
      )}
    </div>
  );
};

const CriteriaInput = ({ label, field, value, onChange, prefix = '', suffix = '', placeholder, lang, typical }) => {
  return (
    <div className="mb-4">
      <label className="flex items-center text-sm font-medium text-slate-700 mb-1.5">
        {label}
        <TooltipButton field={field} lang={lang} />
      </label>
      <div className="relative">
        {prefix && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{prefix}</span>
        )}
        <input
          type="number"
          value={value || ''}
          onChange={(e) => onChange(e.target.value ? parseFloat(e.target.value) : null)}
          placeholder={placeholder}
          className={`w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${prefix ? 'pl-7' : ''} ${suffix ? 'pr-8' : ''}`}
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{suffix}</span>
        )}
      </div>
      {typical && (
        <p className="text-xs text-slate-500 mt-1">
          {lang === 'es' ? 'Rango típico:' : 'Typical range:'} {typical}
        </p>
      )}
    </div>
  );
};

const InvestmentCriteriaOnboarding = ({ isOpen, onClose, onComplete, lang = 'en' }) => {
  const [activeTab, setActiveTab] = useState('flip');
  const [saving, setSaving] = useState(false);
  const [criteria, setCriteria] = useState({
    flip: { ...DEFAULTS.flip },
    rental: { ...DEFAULTS.rental },
    brrrr: { ...DEFAULTS.brrrr }
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await investmentCriteriaAPI.updateCriteria(criteria);
      toast.success(lang === 'en' ? 'Investment criteria saved!' : '¡Criterios de inversión guardados!');
      onComplete?.();
      onClose();
    } catch (error) {
      console.error('Failed to save criteria:', error);
      toast.error(lang === 'en' ? 'Failed to save criteria' : 'Error al guardar criterios');
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = async () => {
    try {
      await investmentCriteriaAPI.completeOnboarding();
      onComplete?.();
      onClose();
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
      onClose();
    }
  };

  const updateFlipCriteria = (field, value) => {
    setCriteria(prev => ({
      ...prev,
      flip: { ...prev.flip, [field]: value }
    }));
  };

  const updateRentalCriteria = (field, value) => {
    setCriteria(prev => ({
      ...prev,
      rental: { ...prev.rental, [field]: value }
    }));
  };

  const updateBRRRRCriteria = (field, value) => {
    setCriteria(prev => ({
      ...prev,
      brrrr: { ...prev.brrrr, [field]: value }
    }));
  };

  if (!isOpen) return null;

  const tabs = [
    { id: 'flip', label: lang === 'es' ? 'Fix & Flip' : 'Fix & Flip', icon: TrendingUp },
    { id: 'rental', label: lang === 'es' ? 'Alquiler' : 'Rental', icon: Home },
    { id: 'brrrr', label: 'BRRRR', icon: DollarSign }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="criteria-onboarding-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <Target className="w-8 h-8" />
              <h2 className="text-xl font-bold">
                {lang === 'es' ? 'Configura Tus Criterios de Inversión' : 'Set Your Investment Criteria'}
              </h2>
            </div>
            <button onClick={handleSkip} className="text-white/80 hover:text-white">
              <X className="w-6 h-6" />
            </button>
          </div>
          <p className="text-blue-100 text-sm">
            {lang === 'es' 
              ? 'Define tus requisitos mínimos para que las recomendaciones de CHEKSTE se personalicen a tus objetivos.'
              : 'Define your minimum requirements so CHEKSTE recommendations are personalized to your goals.'}
          </p>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.id 
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50' 
                  : 'text-slate-600 hover:text-slate-800 hover:bg-slate-50'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* Info Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6 flex items-start gap-2">
            <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-700">
              {lang === 'es'
                ? 'Los campos vacíos usarán los valores predeterminados recomendados. Puedes cambiar estos en cualquier momento desde Configuración.'
                : 'Empty fields will use recommended default values. You can change these anytime from Settings.'}
            </p>
          </div>

          {/* Fix & Flip Tab */}
          {activeTab === 'flip' && (
            <div className="space-y-1">
              <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                {lang === 'es' ? 'Criterios Fix & Flip' : 'Fix & Flip Criteria'}
              </h3>
              <CriteriaInput
                label={lang === 'es' ? 'Ganancia Neta Mínima' : 'Minimum Net Profit'}
                field="min_net_profit"
                value={criteria.flip.min_net_profit}
                onChange={(v) => updateFlipCriteria('min_net_profit', v)}
                prefix="$"
                placeholder="30,000"
                lang={lang}
                typical="$25,000 - $50,000"
              />
              <CriteriaInput
                label={lang === 'es' ? 'Margen de Ganancia Mínimo' : 'Minimum Profit Margin'}
                field="min_profit_margin"
                value={criteria.flip.min_profit_margin}
                onChange={(v) => updateFlipCriteria('min_profit_margin', v)}
                suffix="%"
                placeholder="15"
                lang={lang}
                typical="12% - 20%"
              />
              <CriteriaInput
                label={lang === 'es' ? 'ROI Mínimo' : 'Minimum ROI'}
                field="min_roi"
                value={criteria.flip.min_roi}
                onChange={(v) => updateFlipCriteria('min_roi', v)}
                suffix="%"
                placeholder="20"
                lang={lang}
                typical="15% - 30%"
              />
            </div>
          )}

          {/* Rental Tab */}
          {activeTab === 'rental' && (
            <div className="space-y-1">
              <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Home className="w-5 h-5 text-green-600" />
                {lang === 'es' ? 'Criterios de Alquiler' : 'Rental Criteria'}
              </h3>
              <CriteriaInput
                label={lang === 'es' ? 'Flujo de Caja Mensual Mínimo' : 'Minimum Monthly Cash Flow'}
                field="min_monthly_cash_flow"
                value={criteria.rental.min_monthly_cash_flow}
                onChange={(v) => updateRentalCriteria('min_monthly_cash_flow', v)}
                prefix="$"
                placeholder="250"
                lang={lang}
                typical="$200 - $500"
              />
              <CriteriaInput
                label={lang === 'es' ? 'Cap Rate Mínimo' : 'Minimum Cap Rate'}
                field="min_cap_rate"
                value={criteria.rental.min_cap_rate}
                onChange={(v) => updateRentalCriteria('min_cap_rate', v)}
                suffix="%"
                placeholder="7"
                lang={lang}
                typical="6% - 10%"
              />
              <CriteriaInput
                label={lang === 'es' ? 'Retorno Cash-on-Cash Mínimo' : 'Minimum Cash-on-Cash Return'}
                field="min_cash_on_cash"
                value={criteria.rental.min_cash_on_cash}
                onChange={(v) => updateRentalCriteria('min_cash_on_cash', v)}
                suffix="%"
                placeholder="10"
                lang={lang}
                typical="8% - 15%"
              />
              <CriteriaInput
                label={lang === 'es' ? 'DSCR Mínimo (Opcional)' : 'Minimum DSCR (Optional)'}
                field="min_dscr"
                value={criteria.rental.min_dscr}
                onChange={(v) => updateRentalCriteria('min_dscr', v)}
                placeholder="1.25"
                lang={lang}
                typical="1.2 - 1.5"
              />
            </div>
          )}

          {/* BRRRR Tab */}
          {activeTab === 'brrrr' && (
            <div className="space-y-1">
              <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-purple-600" />
                {lang === 'es' ? 'Criterios BRRRR' : 'BRRRR Criteria'}
              </h3>
              <CriteriaInput
                label={lang === 'es' ? 'Flujo Mínimo Después de Refinanciar' : 'Min Cash Flow After Refinance'}
                field="min_cash_flow_after_refi"
                value={criteria.brrrr.min_cash_flow_after_refi}
                onChange={(v) => updateBRRRRCriteria('min_cash_flow_after_refi', v)}
                prefix="$"
                placeholder="200"
                lang={lang}
                typical="$150 - $300"
              />
              <CriteriaInput
                label={lang === 'es' ? 'Máximo Efectivo en el Trato' : 'Maximum Cash Left in Deal'}
                field="max_cash_left_in_deal"
                value={criteria.brrrr.max_cash_left_in_deal}
                onChange={(v) => updateBRRRRCriteria('max_cash_left_in_deal', v)}
                prefix="$"
                placeholder="25,000"
                lang={lang}
                typical="$0 - $30,000"
              />
              <CriteriaInput
                label={lang === 'es' ? 'LTV Objetivo de Refinanciamiento' : 'Target Refinance LTV'}
                field="target_refi_ltv"
                value={criteria.brrrr.target_refi_ltv}
                onChange={(v) => updateBRRRRCriteria('target_refi_ltv', v)}
                suffix="%"
                placeholder="75"
                lang={lang}
                typical="70% - 80%"
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
          <button
            onClick={handleSkip}
            className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 transition-colors"
          >
            {lang === 'es' ? 'Omitir por ahora' : 'Skip for now'}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
            {lang === 'es' ? 'Guardar Criterios' : 'Save Criteria'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvestmentCriteriaOnboarding;
